import * as THREE from 'three'
import { Direction, GridPos, OPPOSITE } from '../../types'
import { buildBlueBob, buildHatModel, BlueBobModel } from '../../procedural/models'
import { GridMover } from '../GridMover'
import { MOVE_SPEED } from '../../constants/game-config'
import { damp, TAU } from '../../utils/math'
import type { Maze } from '../world/Maze'
import { EquippedCosmetics, EMPTY_EQUIP } from '../../data/cosmetics'

const TRAIL_POOL = 24
const TRAIL_INTERVAL = 0.06 // seconds between trail particles while moving
const TRAIL_LIFE = 0.7
/** Shared powered-glow emissive — copied into the body material, never mutated. */
const POWERED_EMISSIVE = new THREE.Color(0xffe14d)

interface TrailParticle {
  mesh: THREE.Mesh
  life: number
  baseScale: number
}

const YAW: Record<Direction, number> = {
  [Direction.None]: 0,
  [Direction.Up]: Math.PI,
  [Direction.Down]: 0,
  [Direction.Left]: -Math.PI / 2,
  [Direction.Right]: Math.PI / 2,
}

/** The player: grid movement with buffered turns, plus chompy underwater animation. */
export class BlueBob {
  readonly group = new THREE.Group()
  /** World-space group for dropped trail particles (must NOT follow the player). */
  readonly trailGroup = new THREE.Group()
  private model: BlueBobModel
  private mover: GridMover
  private yaw = 0
  private targetYaw = 0
  private chompT = 0
  private bobT = 0
  private walkPhase = 0
  private walkAmp = 0
  private powered = false
  private baseEmissive: THREE.Color
  private baseMetalness: number
  private baseRoughness: number
  desired: Direction = Direction.None
  private startCell: GridPos

  // Cosmetics
  private applied: EquippedCosmetics = { ...EMPTY_EQUIP }
  private isRainbow = false
  private rainbowT = 0
  private trailType: 'trail-bubbles' | 'trail-sparkle' | null = null
  private trailTimer = 0
  private trailPool: TrailParticle[] = []
  private trailMats: Record<'trail-bubbles' | 'trail-sparkle', THREE.Material> | null = null

  constructor(startRow: number, startCol: number, maze: Maze) {
    this.model = buildBlueBob()
    this.group.add(this.model.group)
    this.startCell = { row: startRow, col: startCol }
    this.mover = new GridMover(startRow, startCol, MOVE_SPEED, maze, false)
    const mat = this.model.bodyMat
    this.baseEmissive = mat.emissive.clone()
    this.baseMetalness = mat.metalness
    this.baseRoughness = mat.roughness
    this.syncTransform()
  }

  /** Apply the player's equipped cosmetics (hat / skin / trail). Cheap to call repeatedly;
   * only the slots that changed are rebuilt. */
  applyCosmetics(eq: EquippedCosmetics): void {
    if (eq.hat !== this.applied.hat) this.setHat(eq.hat)
    if (eq.skin !== this.applied.skin) this.setSkin(eq.skin)
    if (eq.trail !== this.applied.trail) this.setTrail(eq.trail)
    this.applied = { ...eq }
  }

  /** What the model is actually wearing (for the E2E harness / diagnostics). */
  cosmeticsInfo(): { hat: string | null; skin: string | null; trail: string | null; hatMeshes: number } {
    let hatMeshes = 0
    this.model.hat.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) hatMeshes++
    })
    return { ...this.applied, hatMeshes }
  }

  private setHat(id: string | null): void {
    for (const child of [...this.model.hat.children]) {
      this.model.hat.remove(child)
      child.traverse((o) => {
        const mesh = o as THREE.Mesh
        if (mesh.geometry) mesh.geometry.dispose()
        const mm = mesh.material
        if (mm) (Array.isArray(mm) ? mm : [mm]).forEach((x) => x.dispose())
      })
    }
    if (id) {
      const hat = buildHatModel(id)
      if (hat) this.model.hat.add(hat)
    }
  }

  private setSkin(id: string | null): void {
    const mat = this.model.bodyMat
    this.isRainbow = id === 'skin-rainbow'
    if (id === 'skin-golden') {
      mat.color.setHex(0xffcf3a)
      mat.metalness = 0.65
      mat.roughness = 0.3
    } else if (id === 'skin-rainbow') {
      mat.metalness = 0.25
      mat.roughness = 0.4 // hue set every frame in animateCosmetics
    } else {
      mat.color.copy(this.model.baseColor)
      mat.metalness = this.baseMetalness
      mat.roughness = this.baseRoughness
    }
  }

  private setTrail(id: string | null): void {
    this.trailType = id === 'trail-bubbles' || id === 'trail-sparkle' ? id : null
    if (this.trailType && !this.trailPool.length) this.initTrailPool()
    if (!this.trailType) for (const p of this.trailPool) this.hideParticle(p)
  }

  private initTrailPool(): void {
    const geo = new THREE.SphereGeometry(0.12, 8, 8)
    this.trailMats = {
      'trail-bubbles': new THREE.MeshStandardMaterial({
        color: 0x9fe4ff,
        transparent: true,
        opacity: 0.55,
        roughness: 0.2,
        emissive: 0x3aa0ff,
        emissiveIntensity: 0.3,
      }),
      'trail-sparkle': new THREE.MeshBasicMaterial({
        color: 0xffe98a,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    }
    for (let i = 0; i < TRAIL_POOL; i++) {
      const mesh = new THREE.Mesh(geo, this.trailMats['trail-bubbles'])
      mesh.visible = false
      this.trailGroup.add(mesh)
      this.trailPool.push({ mesh, life: 0, baseScale: 1 })
    }
  }

  private hideParticle(p: TrailParticle): void {
    p.life = 0
    p.mesh.visible = false
  }

  private spawnTrailParticle(): void {
    if (!this.trailType || !this.trailMats) return
    // Reuse the most-expired slot so a fixed pool never allocates per frame.
    let slot = this.trailPool[0]
    for (const p of this.trailPool) if (p.life < slot.life) slot = p
    slot.mesh.material = this.trailMats[this.trailType]
    slot.mesh.position.set(
      this.mover.x + (Math.random() - 0.5) * 0.18,
      0.15,
      this.mover.z + (Math.random() - 0.5) * 0.18,
    )
    slot.baseScale = this.trailType === 'trail-sparkle' ? 0.55 + Math.random() * 0.4 : 0.8 + Math.random() * 0.5
    slot.mesh.scale.setScalar(slot.baseScale)
    slot.mesh.visible = true
    slot.life = TRAIL_LIFE
  }

  /** Rainbow hue cycle + trail particle spawn/fade. Fades by SHRINKING (scale→0) so the
   * shared per-type material's opacity is never mutated (which would affect all particles). */
  private animateCosmetics(dt: number, moving: boolean): void {
    if (this.isRainbow) {
      this.rainbowT += dt
      this.model.bodyMat.color.setHSL((this.rainbowT * 0.25) % 1, 0.85, 0.56)
    }
    if (!this.trailType) return
    if (moving) {
      this.trailTimer -= dt
      while (this.trailTimer <= 0) {
        this.trailTimer += TRAIL_INTERVAL
        this.spawnTrailParticle()
      }
    }
    for (const p of this.trailPool) {
      if (p.life <= 0) continue
      p.life -= dt
      if (p.life <= 0) {
        this.hideParticle(p)
        continue
      }
      const k = p.life / TRAIL_LIFE
      p.mesh.position.y += dt * 0.5 // drift upward like bubbles
      p.mesh.scale.setScalar(p.baseScale * k)
    }
  }

  get cell(): GridPos {
    return { row: this.mover.row, col: this.mover.col }
  }
  get dir(): Direction {
    return this.mover.dir
  }
  get worldX(): number {
    return this.mover.x
  }
  get worldZ(): number {
    return this.mover.z
  }

  reset(): void {
    this.mover.setCell(this.startCell.row, this.startCell.col)
    this.mover.dir = Direction.None
    this.desired = Direction.None
    this.yaw = this.targetYaw = 0
    this.chompT = this.bobT = this.walkPhase = this.walkAmp = 0
    this.group.scale.setScalar(1)
    this.group.rotation.set(0, 0, 0)
    this.model.group.position.y = 0
    this.model.group.rotation.y = 0
    this.model.mouth.scale.y = 1
    for (const pivot of [...this.model.armPivots, ...this.model.legPivots]) pivot.rotation.x = 0
    this.setPowered(false)
    this.trailTimer = 0
    for (const p of this.trailPool) this.hideParticle(p) // clear dropped trail on respawn/level change
    this.syncTransform()
  }

  /** Release GPU resources owned by this player: the per-instance cloned body material, the
   * shared trail-particle geometry, and the per-type trail materials. Call from scene teardown.
   * (BlueBob is created once per app today, so this guards future recreation/teardown paths.) */
  dispose(): void {
    // Detach + hide the pooled particle meshes BEFORE disposing their shared geometry, so no
    // orphan stays parented to trailGroup referencing freed GPU resources (WebGL warnings) and a
    // future re-init can't layer a second batch on top of the first.
    for (const p of this.trailPool) {
      p.mesh.visible = false
      this.trailGroup.remove(p.mesh)
    }
    if (this.trailPool.length) this.trailPool[0].mesh.geometry.dispose() // pool shares one geometry
    if (this.trailMats) for (const m of Object.values(this.trailMats)) m.dispose()
    this.model.bodyMat.dispose()
    this.powered = false // a later setPowered() must not mutate the now-disposed body material
    this.trailPool = []
    this.trailMats = null
    this.trailType = null // so a post-dispose re-apply of the same trail id re-inits cleanly
  }

  setSpeed(s: number): void {
    this.mover.speed = s
  }

  setPowered(on: boolean): void {
    if (this.powered === on) return
    this.powered = on
    const mat = this.model.body.material as THREE.MeshStandardMaterial
    // copy() into the existing emissive object — no per-toggle Color allocation.
    mat.emissive.copy(on ? POWERED_EMISSIVE : this.baseEmissive)
    mat.emissiveIntensity = on ? 0.6 : 1
  }

  update(dt: number): void {
    this.mover.update(dt, (cell, curDir) => {
      // Prefer the buffered desired direction if walkable; else keep going; else stop.
      if (this.desired !== Direction.None && this.mover.canMove(this.desired)) return this.desired
      if (curDir !== Direction.None && this.mover.canMove(curDir)) return curDir
      // allow immediate reverse if requested into a wall-free cell
      if (this.desired !== Direction.None && this.desired === OPPOSITE[curDir]) return this.desired
      return Direction.None
    })

    if (this.mover.dir !== Direction.None) this.targetYaw = YAW[this.mover.dir]
    this.syncTransform()

    // Animation: yaw toward heading, chomp + bob.
    this.yaw = dampAngle(this.yaw, this.targetYaw, 12, dt)
    this.model.group.rotation.y = this.yaw

    const moving = this.mover.dir !== Direction.None
    this.bobT += dt
    this.model.group.position.y = Math.sin(this.bobT * 4) * 0.04
    if (moving) {
      this.chompT += dt * 12
      const chomp = (Math.sin(this.chompT) * 0.5 + 0.5) * 0.5 + 0.5
      this.model.mouth.scale.y = chomp
    } else {
      this.model.mouth.scale.y = damp(this.model.mouth.scale.y, 1, 8, dt)
    }

    this.animateWalk(dt, moving)
    this.animateCosmetics(dt, moving)
  }

  /** Swing arms + legs about their joints. Step frequency tracks ground speed (faster
   * movement = faster strides) and amplitude scales with speed; both settle to 0 at rest. */
  private animateWalk(dt: number, moving: boolean): void {
    const speedRatio = this.mover.speed / MOVE_SPEED
    // sin() has period TAU and the swing coefficient is 1, so wrapping here keeps the
    // pose identical while preventing unbounded float growth over a long session.
    if (moving) this.walkPhase = (this.walkPhase + dt * this.mover.speed * 2.2) % TAU
    const targetAmp = moving ? 0.55 * Math.min(1.4, speedRatio) : 0
    this.walkAmp = damp(this.walkAmp, targetAmp, 10, dt)
    const swing = Math.sin(this.walkPhase) * this.walkAmp
    const [lLeg, rLeg] = this.model.legPivots
    const [lArm, rArm] = this.model.armPivots
    // legs alternate; arms counter-swing to the legs for a natural gait.
    lLeg.rotation.x = swing
    rLeg.rotation.x = -swing
    lArm.rotation.x = -swing * 0.8
    rArm.rotation.x = swing * 0.8
  }

  /** Current limb swing (radians) for diagnostics / the E2E harness. */
  get limbSwing(): { leg: number; arm: number } {
    return { leg: this.model.legPivots[0].rotation.x, arm: this.model.armPivots[0].rotation.x }
  }

  /** Ambient bob only — never advances grid movement (attract/menus/pause). */
  idle(dt: number): void {
    this.bobT += dt
    this.model.group.position.y = Math.sin(this.bobT * 2) * 0.05
    this.yaw = dampAngle(this.yaw, this.targetYaw, 4, dt)
    this.model.group.rotation.y = this.yaw
    this.model.mouth.scale.y = damp(this.model.mouth.scale.y, 1, 6, dt)
    this.animateWalk(dt, false) // relax arms/legs to rest while idling
    this.animateCosmetics(dt, false) // rainbow keeps cycling on menus; no trail without movement
  }

  private syncTransform(): void {
    this.group.position.x = this.mover.x
    this.group.position.z = this.mover.z
  }
}

function dampAngle(a: number, b: number, lambda: number, dt: number): number {
  let diff = (b - a) % TAU
  if (diff > Math.PI) diff -= TAU
  if (diff < -Math.PI) diff += TAU
  return a + diff * (1 - Math.exp(-lambda * dt))
}
